import { exhaustiveGuard } from '@/utils/function-utils';
import { useEffect, useMemo, useState } from 'react';

/**
 * Returns the number of milliseconds until the start of the next hour.
 * @param date - The date to calculate from.
 * @returns The number of milliseconds.
 */
function getMsUntilStartNextHour(date: Date): number {
  return (
    (60 - date.getMinutes()) * 60 * 1000 -
    date.getSeconds() * 1000 -
    date.getMilliseconds()
  );
}

/**
 * Returns the number of milliseconds until the start of the next minute.
 * @param date - The date to calculate from.
 * @returns The number of milliseconds.
 */
function getMsUntilStartNextMinute(date: Date): number {
  return (60 - date.getSeconds()) * 1000 - date.getMilliseconds();
}

/**
 * Returns the number of milliseconds until the start of the next second.
 * @param date - The date to calculate from.
 * @returns The number of milliseconds.
 */
function getMsUntilStartNextSecond(date: Date): number {
  return 1000 - date.getMilliseconds();
}

/**
 * Returns the number of milliseconds until the next checkpoint based on the specified granularity.
 * @param date - The date to calculate from.
 * @param granularity - The granularity of the checkpoint.
 * @returns The number of milliseconds.
 */
function getMsUntilNextCheckpoint(
  date: Date,
  granularity: 'hour' | 'minute' | 'second'
): number {
  switch (granularity) {
    case 'hour':
      return getMsUntilStartNextHour(date);
    case 'minute':
      return getMsUntilStartNextMinute(date);
    case 'second':
      return getMsUntilStartNextSecond(date);
  }
  return exhaustiveGuard(
    granularity,
    `"${granularity}" is an invalid granularity`
  );
}

/**
 * A hook that returns a computed value that updates at a specified time interval.
 * @param factory - A function that returns the value to be memoized.
 * @param granularity - The granularity of the state updates.
 * @returns The computed value.
 */
export function useTimedMemo<T>(
  factory: () => T,
  granularity: 'hour' | 'minute' | 'second'
): T {
  const [cacheBuster, forceRerender] = useState({});
  // Recompute the state whenever the factory function or cache buster changes.
  const state = useMemo(factory, [factory, cacheBuster]);

  useEffect(
    function initRevalidationTimer() {
      let timeout: number;
      /** Restarts the timer to revalidate the state at the next checkpoint. */
      function restartTimer(): void {
        const now = new Date();
        const msUntilNextCheckpoint = getMsUntilNextCheckpoint(
          now,
          granularity
        );

        timeout = window.setTimeout(() => {
          forceRerender({});
          restartTimer();
        }, msUntilNextCheckpoint);
      }

      restartTimer();
      return () => window.clearTimeout(timeout);
    },
    [granularity]
  );

  return state;
}
